// Mesure d'audience anonyme (Matomo Cloud) : sans cookie, « Ne pas suivre » respecté, aucune
// donnée personnelle. Voir les pages Mentions légales et Confidentialité.
//
// Le numéro de site Matomo vient du build (REACT_APP_MATOMO_SITE_ID, posé dans les workflows de
// déploiement : 1 = sappm.fr en production, 2 = dev.sappm.fr), jamais du code. Sans cette variable
// (développement local, tests), rien n'est chargé ni envoyé.

const MATOMO_URL = "https://sappm.matomo.cloud/";
const MATOMO_SCRIPT = "https://cdn.matomo.cloud/sappm.matomo.cloud/matomo.js";

// Seuls événements autorisés : jamais de réponse ni de score (le GHQ-12 est une donnée de santé).
const TEST_NAMES = ["qvt", "ghq12", "ia-agents"];

// Routes dont la fin du chemin est un jeton secret : il ne doit jamais partir chez un tiers.
const TOKEN_ROUTES = ["/reset-password/", "/verify-email/", "/confirm-email-change/", "/complete-registration/", "/invite/"];

// Refus du suivi choisi par l'agent (lien « Refuser le suivi » des Mentions légales). Mémorisé côté
// navigateur, sur sappm.fr même : un cookie tiers de Matomo serait bloqué par de nombreux navigateurs.
const OPT_OUT_KEY = "paroles_agents_analytics_optout";

let initialized = false;
let lastTrackedPath = null;
// Refus pris pendant la visite en cours, même si le localStorage est indisponible.
let optedOutThisSession = false;

function getSiteId() {
    return process.env.REACT_APP_MATOMO_SITE_ID || "";
}

export function isOptedOut() {
    try {
        return window.localStorage.getItem(OPT_OUT_KEY) === "1";
    } catch (e) {
        return false;
    }
}

export function setOptOut(optedOut) {
    try {
        if (optedOut) window.localStorage.setItem(OPT_OUT_KEY, "1");
        else window.localStorage.removeItem(OPT_OUT_KEY);
    } catch (e) {
        // localStorage indisponible : le choix vaut pour la page en cours (voir isEnabled)
    }
    if (getSiteId() === "") return;
    if (optedOut) {
        optedOutThisSession = true;
        push("optUserOut");
        return;
    }
    // Ré-acceptation : effective tout de suite, sans attendre un rechargement.
    optedOutThisSession = false;
    if (initialized) {
        push("forgetUserOptOut");
        lastTrackedPath = null;
        trackPageViewIfChanged();
    } else {
        // La visite avait démarré avec le refus déjà enregistré : Matomo n'a pas été chargé.
        initAnalytics();
    }
}

function isEnabled() {
    return getSiteId() !== "" && !optedOutThisSession && !isOptedOut();
}

function push(...args) {
    window._paq = window._paq || [];
    window._paq.push(args);
}

// Chemin envoyé à Matomo : sans paramètres de requête ni ancre, jetons masqués.
export function sanitizePath(pathname) {
    const route = TOKEN_ROUTES.find(prefix => pathname.startsWith(prefix));
    return route ? `${route}:token` : pathname;
}

function trackPageViewIfChanged() {
    if (!isEnabled()) return;
    const path = sanitizePath(window.location.pathname);
    if (path === lastTrackedPath) return;
    lastTrackedPath = path;
    push("setCustomUrl", window.location.origin + path);
    push("setDocumentTitle", document.title);
    push("trackPageView");
}

export function initAnalytics() {
    if (!isEnabled() || initialized) return;
    initialized = true;

    push("setDoNotTrack", true);
    push("disableCookies");
    push("enableLinkTracking");
    push("setTrackerUrl", MATOMO_URL + "matomo.php");
    push("setSiteId", getSiteId());

    const script = document.createElement("script");
    script.async = true;
    script.src = MATOMO_SCRIPT;
    document.head.appendChild(script);

    // Navigation SPA : l'appli change de page par pushState / replaceState (et popstate pour « Retour »).
    // La page vue est envoyée au tour suivant : le titre du document n'est lu qu'une fois la page rendue.
    ["pushState", "replaceState"].forEach(method => {
        const original = window.history[method];
        window.history[method] = function (...args) {
            const result = original.apply(this, args);
            setTimeout(trackPageViewIfChanged, 0);
            return result;
        };
    });
    window.addEventListener("popstate", () => setTimeout(trackPageViewIfChanged, 0));

    trackPageViewIfChanged();
}

function trackEvent(...args) {
    if (!isEnabled()) return;
    push("trackEvent", ...args);
}

export function trackTestStarted(testName) {
    if (TEST_NAMES.includes(testName)) trackEvent("Test", "Commence", testName);
}

export function trackTestFinished(testName) {
    if (TEST_NAMES.includes(testName)) trackEvent("Test", "Termine", testName);
}

export function trackSignupSucceeded() {
    trackEvent("Inscription", "Reussie");
}

// Réservé aux tests : remet le module dans son état initial.
export function _resetForTests() {
    initialized = false;
    lastTrackedPath = null;
    optedOutThisSession = false;
}
